import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import StatusBadge from "@/components/StatusBadge";
import { STREAMS } from "@/constants/testIds";
import { t } from "@/lib/i18n";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Plus, Trash2, Radio, Search, MessageCircle, Heart, Play, Square, Pencil } from "lucide-react";

const emptyForm = {
  stream_url: "",
  keywords: [],
  target_accounts: [],
  approach: "mixed",
  watch_time_min: 4,
  watch_time_max: 11,
  like_probability: 0.4,
  comment_probability: 0.15,
};

export default function Streams() {
  const [list, setList] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [s, a] = await Promise.all([api.streams(), api.accounts()]);
      setList(s); setAccounts(a);
    } catch { toast.error("Не удалось загрузить"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (s) => {
    setEditing(s.id);
    setForm({
      stream_url: s.stream_url,
      keywords: s.keywords || [],
      target_accounts: s.target_accounts || [],
      approach: s.approach || "mixed",
      watch_time_min: s.watch_time_min ?? 4,
      watch_time_max: s.watch_time_max ?? 11,
      like_probability: s.like_probability ?? 0.4,
      comment_probability: s.comment_probability ?? 0.15,
    });
    setOpen(true);
  };

  const submit = async () => {
    if (!form.stream_url) return toast.error("URL стрима обязателен");
    if (form.target_accounts.length === 0) return toast.error("Выбери хотя бы один аккаунт");
    const payload = {
      ...form,
      keywords: Array.isArray(form.keywords) ? form.keywords : (form.keywords || "").split(",").map((s) => s.trim()).filter(Boolean),
    };
    try {
      if (editing) await api.updateStream(editing, payload);
      else await api.createStream(payload);
      toast.success(t.common.saved);
      setOpen(false); setForm(emptyForm); setEditing(null);
      load();
    } catch { toast.error(t.common.saveFailed); }
  };

  const del = async (id) => {
    if (!window.confirm(t.common.confirmDelete)) return;
    try { await api.deleteStream(id); load(); }
    catch { toast.error(t.common.failed); }
  };

  const start = async (id) => {
    try { await api.startStream(id); toast.success("Задача запущена"); load(); }
    catch { toast.error(t.common.failed); }
  };
  const stop = async (id) => {
    try { await api.stopStream(id); toast.success("Задача остановлена"); load(); }
    catch { toast.error(t.common.failed); }
  };

  const toggleAccount = (id) => {
    setForm((f) => ({
      ...f,
      target_accounts: f.target_accounts.includes(id)
        ? f.target_accounts.filter((x) => x !== id)
        : [...f.target_accounts, id],
    }));
  };

  return (
    <div data-testid={STREAMS.root}>
      <PageHeader
        title={t.streams.title}
        description={t.streams.description}
        actions={
          <button
            data-testid={STREAMS.addBtn}
            onClick={openNew}
            className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-medium transition-colors"
          >
            <Plus className="h-4 w-4" /> {t.streams.add}
          </button>
        }
      />

      {loading ? (
        <div className="text-slate-400 text-sm">{t.common.loading}</div>
      ) : list.length === 0 ? (
        <div className="card-soft p-10 text-center">
          <Radio className="h-10 w-10 text-slate-300 mx-auto" />
          <div className="font-display text-lg mt-3 text-slate-900">{t.streams.empty}</div>
          <div className="text-sm text-slate-500 mt-1">{t.streams.emptyHint}</div>
        </div>
      ) : (
        <div className="space-y-3">
          {list.map((s) => (
            <div key={s.id} className="card-soft p-5">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Radio className="h-4 w-4 text-emerald-500" />
                    <div className="text-sm font-mono text-slate-900 truncate">{s.stream_url}</div>
                    <StatusBadge status={s.status} />
                  </div>
                  <div className="text-xs text-slate-500 mt-1">
                    Способ: <span className="text-slate-700 font-medium">{t.streams.approach[s.approach] || s.approach}</span> · Смотрят {s.watch_time_min}–{s.watch_time_max} мин
                  </div>
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {(s.keywords || []).map((k) => (
                      <span key={k} className="text-[11px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-700">
                        <Search className="inline h-3 w-3 mr-0.5" /> {k}
                      </span>
                    ))}
                  </div>
                  <div className="flex items-center gap-4 mt-3 text-xs text-slate-500">
                    <span><Heart className="inline h-3 w-3 mr-1" /> лайк {Math.round(s.like_probability * 100)}%</span>
                    <span><MessageCircle className="inline h-3 w-3 mr-1" /> коммент {Math.round(s.comment_probability * 100)}%</span>
                    <span>{(s.target_accounts || []).length} аккаунтов</span>
                  </div>
                </div>
                <div className="flex gap-2 shrink-0">
                  {s.status !== "running" ? (
                    <button
                      data-testid={STREAMS.startBtn(s.id)}
                      onClick={() => start(s.id)}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-emerald-100 hover:bg-emerald-200 text-emerald-700 text-xs font-medium transition-colors"
                    >
                      <Play className="h-3.5 w-3.5" /> {t.streams.start}
                    </button>
                  ) : (
                    <button
                      data-testid={STREAMS.stopBtn(s.id)}
                      onClick={() => stop(s.id)}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-amber-100 hover:bg-amber-200 text-amber-700 text-xs font-medium transition-colors"
                    >
                      <Square className="h-3.5 w-3.5" /> {t.streams.stop}
                    </button>
                  )}
                  <button
                    data-testid={STREAMS.editBtn(s.id)}
                    onClick={() => openEdit(s)}
                    className="text-slate-400 hover:text-emerald-600 transition-colors p-1.5"
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button
                    data-testid={STREAMS.deleteBtn(s.id)}
                    onClick={() => del(s.id)}
                    className="text-slate-300 hover:text-rose-500 transition-colors p-1.5"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-2xl max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display">
              {editing ? t.streams.edit : t.streams.add}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500">{t.streams.fields.url}</Label>
              <Input value={form.stream_url} onChange={(e) => setForm({ ...form, stream_url: e.target.value })} className="mt-1 font-mono text-sm" placeholder="https://tiktok.com/@you/live" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500">{t.streams.fields.keywords}</Label>
              <Input
                value={Array.isArray(form.keywords) ? form.keywords.join(", ") : form.keywords}
                onChange={(e) => setForm({ ...form, keywords: e.target.value.split(",").map((s) => s.trim()) })}
                className="mt-1"
                placeholder="crypto, trading, btc"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500">{t.streams.fields.approach}</Label>
              <Select value={form.approach} onValueChange={(v) => setForm({ ...form, approach: v })}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(t.streams.approach).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-widest text-slate-500">{t.streams.fields.minWatch}</Label>
                <Input type="number" value={form.watch_time_min} onChange={(e) => setForm({ ...form, watch_time_min: Number(e.target.value) })} className="mt-1" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-slate-500">{t.streams.fields.maxWatch}</Label>
                <Input type="number" value={form.watch_time_max} onChange={(e) => setForm({ ...form, watch_time_max: Number(e.target.value) })} className="mt-1" />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500">{t.streams.fields.likeProb}: {Math.round(form.like_probability * 100)}%</Label>
              <Slider value={[form.like_probability * 100]} onValueChange={(v) => setForm({ ...form, like_probability: v[0] / 100 })} max={100} step={5} className="mt-2" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500">{t.streams.fields.commentProb}: {Math.round(form.comment_probability * 100)}%</Label>
              <Slider value={[form.comment_probability * 100]} onValueChange={(v) => setForm({ ...form, comment_probability: v[0] / 100 })} max={100} step={5} className="mt-2" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500">{t.streams.fields.accounts}</Label>
              <div className="mt-2 flex flex-wrap gap-2">
                {accounts.map((a) => {
                  const on = form.target_accounts.includes(a.id);
                  return (
                    <button
                      key={a.id}
                      onClick={() => toggleAccount(a.id)}
                      className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                        on
                          ? "bg-emerald-500 border-emerald-500 text-white"
                          : "bg-white border-slate-200 text-slate-700 hover:border-emerald-300"
                      }`}
                    >
                      {a.username}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
          <DialogFooter>
            <button
              data-testid={STREAMS.createBtn}
              onClick={submit}
              className="px-4 py-2 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-medium transition-colors"
            >
              {editing ? t.common.saveChanges : t.streams.create}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
